


import * as THREE from 'three';
import { useLoader, useThree, useFrame } from '@react-three/fiber';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Html , Text3D , Stars  } from '@react-three/drei';
import { EffectComposer, Glitch, Noise, DepthOfField,Bloom,Vignette } from '@react-three/postprocessing';
import { GlitchMode, BlendFunction } from 'postprocessing'; 
import helvetiker from './assets/HACKED_Regular.json'

export default function Earth({ scale = 1,trigger = false }) {
  const earthTexture = useLoader(THREE.TextureLoader, './Earth Texture.png');
  const logoTexture = useLoader(THREE.TextureLoader, './logo.png');
  const { camera, gl } = useThree();

  const sphereRef = useRef();
  const sphereRef2 = useRef();
  const logoPlaneRef = useRef();


  const [countdown, setCountdown] = useState(3000);
  const targetScale = 1.5;
  const duration = 4000;

  const logoAnimRef = useRef({ from: 0, to: targetScale, start: null });
  const logoCurrentScaleRef = useRef(0);
  const logoReverseRef = useRef(false); 

  // const [glitchActive, setGlitchActive] = useState(false);
  // const glitchTimerRef = useRef(null);
  // const glitchIntervalRef = useRef(null);

  // useEffect(() => {
  //   if (glitchIntervalRef.current) return;
  //   const onPulse = () => {
  //     setGlitchActive(true);
  //     if (glitchTimerRef.current) clearTimeout(glitchTimerRef.current);
  //     glitchTimerRef.current = setTimeout(() => setGlitchActive(false), 6000);
  //   };
  //   glitchIntervalRef.current = setInterval(onPulse, 8000);
  //   onPulse();
  //   return () => {
  //     if (glitchIntervalRef.current) { clearInterval(glitchIntervalRef.current); glitchIntervalRef.current = null; }
  //     if (glitchTimerRef.current) { clearTimeout(glitchTimerRef.current); glitchTimerRef.current = null; }
  //   };
  // }, []);

  
  const digitCount = 80;

  const digitRefs = useRef([]);
  const materialRefs = useRef([]);

  const positionsRef = useRef([]);
  const directionsRef = useRef([]);
  const rotationsRef = useRef([]);
  const spinsRef = useRef([]);

  const expandingRef = useRef(false);
  const digitsStartRef = useRef(null);
  const reverseRef = useRef(false);

  const randomDigits = useMemo(
    () => Array.from({ length: digitCount }, () => Math.floor(Math.random() * 10).toString()), []
  );

  const [digitPositions, setDigitPositions] = useState(
    () => Array.from({ length: digitCount }, () => new THREE.Vector3(0, 0, 0))
  );

  const basePositions = useMemo(() => {
    const radius = 3;
    const spread = 2;
    return Array.from({ length: 2 }, () => {
      const angle = Math.random() * Math.PI * 2;
      const x = Math.cos(angle) * radius + THREE.MathUtils.randFloatSpread(spread);
      const y = THREE.MathUtils.randFloatSpread(1.5);
      const z = Math.sin(angle) * radius + THREE.MathUtils.randFloatSpread(spread);
      return new THREE.Vector3(x, y, z);
    });
  }, []);
  const animatedPositions = useRef(basePositions.map((pos) => pos.clone()));

  const easeInOutCubic = (t) => (t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3)/2);
  const startLogoTween = (to) => {
    const from = logoCurrentScaleRef.current;
    logoAnimRef.current = { from, to, start: performance.now() };
  };

  const initDigitState = (inverse = false) => {
    const speedMin = 0.018, speedMax = 0.04;

    if (!inverse) {
      positionsRef.current  = Array.from({ length: digitCount }, () => new THREE.Vector3(0, 0, 0));
      directionsRef.current = Array.from({ length: digitCount }, () =>
        new THREE.Vector3().randomDirection().multiplyScalar(speedMin + Math.random() * (speedMax - speedMin))
      );
    } else {
      const spawnR = 3.0;
      positionsRef.current  = Array.from({ length: digitCount }, () => {
        const dir = new THREE.Vector3().randomDirection();
        const r = spawnR * (0.6 + Math.random() * 0.6);
        return dir.multiplyScalar(r);
      });
      directionsRef.current = positionsRef.current.map((p) =>
        p.clone().normalize().multiplyScalar(-(speedMin + Math.random() * (speedMax - speedMin)))
      );
    }

    rotationsRef.current  = Array.from({ length: digitCount }, () =>
      new THREE.Euler(Math.random()*Math.PI, Math.random()*Math.PI, Math.random()*Math.PI)
    );
    spinsRef.current      = Array.from({ length: digitCount }, () =>
      new THREE.Vector3((Math.random()-0.5)*0.02, (Math.random()-0.3)*0.04, (Math.random()-0.5)*0.02)
    );

    for (let i = 0; i < digitCount; i++) {
      const mesh = digitRefs.current[i];
      const mat  = materialRefs.current[i];
      if (mesh) {
        mesh.visible = true;
        mesh.position.copy(positionsRef.current[i]);
        mesh.rotation.set(
          rotationsRef.current[i].x,
          rotationsRef.current[i].y,
          rotationsRef.current[i].z
        );
        mesh.scale.setScalar(inverse ? 1 : 0.001);
      }
      if (mat) { mat.transparent = true; mat.opacity = 1; }
    }
  };

  const resetDigits = useCallback((inverse = false) => {
    reverseRef.current = inverse;
    initDigitState(inverse);
    expandingRef.current = true;
    digitsStartRef.current = performance.now();

    setDigitPositions(
      Array.from({ length: digitCount }, () => new THREE.Vector3(0, 0, 0))
    );
  }, [digitCount]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      resetDigits(false);
      logoReverseRef.current = false;
      startLogoTween(targetScale);
      setCountdown(duration);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [resetDigits]);

  useEffect(() => {
    const handleClick = () => {
      const shrinking = !logoReverseRef.current;
      logoReverseRef.current = shrinking;
      resetDigits(shrinking);
      startLogoTween(shrinking ? 0 : targetScale);
      setCountdown(duration);
    };
    gl.domElement.addEventListener('click', handleClick);
    return () => gl.domElement.removeEventListener('click', handleClick);
  }, [gl, resetDigits]);

  useFrame(() => {
    const now = performance.now();

    sphereRef.current.rotation.y += 0.002;
    sphereRef2.current.rotation.y -= 0.002;

    animatedPositions.current.forEach((pos, i) => {
      pos.y = basePositions[i].y + Math.sin(now * 0.001 + i) * 0.1;
    });

    // logo tween
    if (logoPlaneRef.current) {
      logoPlaneRef.current.lookAt(camera.position);
      const { from, to, start } = logoAnimRef.current;
      if (start != null) {
        const p = Math.min(1, (now - start) / duration);
        const eased = easeInOutCubic(p);
        const scaleValue = THREE.MathUtils.lerp(from, to, eased);
        logoPlaneRef.current.scale.setScalar(scaleValue);
        logoCurrentScaleRef.current = scaleValue;
        setCountdown(Math.max(0, duration - (now - start)));
      }
    }

    // digits animate
    if (expandingRef.current && digitsStartRef.current) {
      const t = now - digitsStartRef.current;
      const drag = 0.995;
      const appear = Math.min(1, t / 300);

      for (let i = 0; i < digitCount; i++) {
        const mesh = digitRefs.current[i];
        if (!mesh) continue;

        const pos = positionsRef.current[i];
        const vel = directionsRef.current[i];
        pos.add(vel);
        vel.multiplyScalar(drag);

        const rot = rotationsRef.current[i];
        const spin = spinsRef.current[i];
        rot.x += spin.x; rot.y += spin.y; rot.z += spin.z;

        mesh.position.copy(pos);
        mesh.rotation.set(rot.x, rot.y, rot.z);

        const baseScale = 1;
        const s = reverseRef.current ? baseScale : THREE.MathUtils.lerp(0.001, baseScale, appear);
        mesh.scale.setScalar(s);
      }

      const expandMs = 8000;
      const fadeMs   = 2000;
      if (t >= expandMs) {
        const fadeT = Math.min(1, (t - expandMs) / fadeMs);
        const fadeScale = THREE.MathUtils.lerp(1, 0, fadeT);
        const fadeOpacity = THREE.MathUtils.lerp(1, 0, fadeT);
        for (let i = 0; i < digitCount; i++) {
          const mesh = digitRefs.current[i];
          const mat  = materialRefs.current[i];
          if (mesh) mesh.scale.setScalar(fadeScale);
          if (mat)  { mat.opacity = fadeOpacity; mat.needsUpdate = true; }
        }
        if (fadeT >= 1) {
          expandingRef.current = false;
          for (let i = 0; i < digitCount; i++) {
            const mesh = digitRefs.current[i];
            if (mesh) mesh.visible = false;
          }
        }
      }
    }
  });



  const formattedTime = () => {
    const ms = Math.max(0, countdown);
    const minutes = String(Math.floor(ms / 60000)).padStart(2, '0');
    const seconds = String(Math.floor((ms % 60000) / 1000)).padStart(2, '0');
   
    const centis = String(Math.floor((ms % 1000) / 10)).padStart(2, '0');
    return `${minutes}.${seconds}.${centis}`;
  };

  return (
    <>
<Stars
  radius={20}        
  depth={90}         
  count={6000}       
  factor={1}          
  saturation={0}
  fade
  speed={0}          
/>



    
   


      
      <fogExp2 attach="fog" args={['#000000', 0.005]} />

      <directionalLight position={[1, 2, 3]} intensity={2} castShadow shadow-mapSize={[1024, 1024]} />
       <pointLight position={[0, 0, 0]} intensity={5} color="#fffef9" />
       
      <ambientLight intensity={0.05} />
      <directionalLight position={[5, 10, -3]} intensity={1.2} color="#f8f8f8" castShadow />

      <mesh scale={scale} ref={sphereRef}>
        <sphereGeometry args={[1, 64, 64]} />
        <meshStandardMaterial map={earthTexture} transparent opacity={0.25} side={THREE.DoubleSide} />
      </mesh>
      <mesh scale={scale * 1.5} ref={sphereRef2}>
        <sphereGeometry args={[1, 64, 64]} />
        <meshStandardMaterial map={earthTexture} transparent opacity={0.25} side={THREE.DoubleSide} />
      </mesh>

      {/* Floating Counters */}
      {animatedPositions.current.map((pos, i) => (
        <Html key={i} position={pos}>
          <div
            style={{
              fontFamily: 'hacked_kerX',
              fontSize: '1.5rem',
              color: 'white',
              background: 'rgba(0,0,0,0.1)',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              backdropFilter: 'blur(2px)',
            }}
          >
            {formattedTime()}
          </div>
        </Html>
      ))}

      {/* Logo Plane */}
      <mesh ref={logoPlaneRef} scale={[0, 0, 0]} renderOrder={999}>
        <planeGeometry args={[3, 3]} />
        <meshBasicMaterial
          map={logoTexture}
          transparent
          toneMapped={false}
          depthTest={false}
          depthWrite={false}
        />
      </mesh>

      {/* Text3D Digits */}
      {digitPositions.map((pos, i) => (
        <Text3D
          key={i}
          ref={(el) => (digitRefs.current[i] = el)}
          position={pos}
          font={helvetiker}
          size={0.1}
          height={0.02}
        >
          {randomDigits[i]}
          <meshStandardMaterial
            ref={(el) => (materialRefs.current[i] = el)}
            color="white"
            transparent
            opacity={1}
          />
        </Text3D>
      ))}

     {/* postprocessing */}
      <EffectComposer disableNormalPass multisampling={0}>
        <DepthOfField
          target={[0,0,-2]}
          focusDistance={5}  
          focalLength={0.01}    
          bokehScale={1}      
          height={700}          
        />

      <Bloom intensity={0.3} luminanceThreshold={0.8} luminanceSmoothing={0.3} />
      <Vignette eskil={false} offset={0.3} darkness={0.55} />

        <Glitch
          enabled
          mode={GlitchMode.SPORADIC}
          delay={[5, 5]}
          duration={[0.5, 0.5]}
          strength={[0.1, 0.1]}
          ratio={0.2}
        />
       
        <Noise premultiply blendFunction={BlendFunction.OVERLAY} opacity={0.12} />
      </EffectComposer>
    </>
  );
}





